/*
 * Copyright (c) 2023 Anvilor Solutions LLC - All Rights Reserved.
 *
 * The information and source code contained herein is the exclusive property of
 * Anvilor Solutions LLC and may not be disclosed, examined, or reproduced in whole
 * or in part without the explicit written authorization from Anvilor Solutions LLC.
 */

import { AnvilorFormsAppStore } from "../anvilor-forms-app-store.service";
import { AnvilorFormsAppStoreContext } from "../anvilor-forms-app-store.service";
import { ThemeService } from "@anvilor/ngx-theming";
import { StandardThemeConfig } from "@anvilor/ngx-theming";
import { Observable } from "rxjs";
import { tap } from "rxjs";
import { filterUndefined } from "@anvilor/ngx-core-utils";
import { rstrip } from "@anvilor/ngx-core-utils";
import { map } from "rxjs/operators";
import { AnvilorFormDisplayConfig } from "../../models/anvilor-form-document";

export class AnvilorFormsAppTheming {
  private theme: ThemeService;
  private store: AnvilorFormsAppStore;
  private themeConfig$: Observable<StandardThemeConfig>;

  readonly appBackground$: Observable<string | undefined>;

  /**
   * A text color that contrasts the custom page background, so content sitting
   * directly on it (e.g. a form's title/subtitle, outside any panel) stays
   * legible. `undefined` when the form paints no solid background — then the
   * theme's own on-background color applies.
   */
  readonly appForeground$: Observable<string | undefined>;

  constructor(ctx: AnvilorFormsAppStoreContext) {
    this.theme = ctx.theme;
    this.store = ctx.store;
    this.themeConfig$ = this.store
      .selectFromDisplayConfig((config) => config.theme_config)
      .pipe(filterUndefined());

    this.appBackground$ = ctx.store.displayConfig$.pipe(
      map((c) => this.getBackgroundFromDisplayConfig(c)),
    );
    this.appForeground$ = ctx.store.displayConfig$.pipe(
      map((c) => this.getForegroundFromDisplayConfig(c)),
    );
  }

  bindTheme() {
    this.store.subscribeTo(
      this.themeConfig$.pipe(tap((config) => this.theme.updateFromConfig(config))),
    );
  }

  private getBackgroundFromDisplayConfig(
    displayConfig: AnvilorFormDisplayConfig,
  ): string | undefined {
    const bgImage = displayConfig.background_image;
    if (bgImage) {
      if (bgImage.startsWith("https://")) {
        return `url("${bgImage}")`;
      } else {
        console.warn(`Ignoring non https background image: ${bgImage}`);
      }
    }
    if (displayConfig.background_color) {
      return rstrip(displayConfig.background_color, ";");
    }
    return undefined;
  }

  private getForegroundFromDisplayConfig(
    displayConfig: AnvilorFormDisplayConfig,
  ): string | undefined {
    // Only override text color for a solid custom background. A background
    // image's luminance is unknowable, and with no custom background the theme
    // already provides a matching on-background color.
    if (displayConfig.background_image || !displayConfig.background_color) {
      return undefined;
    }
    return contrastingForeground(rstrip(displayConfig.background_color, ";"));
  }
}

/** sRGB channel → linear light (WCAG relative-luminance step). */
function _toLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** Parse a `#rgb`/`#rrggbb`/`rgb(...)` color to [r,g,b], or null if unparseable. */
function _parseRgb(color: string): [number, number, number] | null {
  const s = color.trim();
  const hex = s.replace(/^#/, "");
  if (/^[0-9a-fA-F]{3}$/.test(hex)) {
    return [
      parseInt(hex[0] + hex[0], 16),
      parseInt(hex[1] + hex[1], 16),
      parseInt(hex[2] + hex[2], 16),
    ];
  }
  if (/^[0-9a-fA-F]{6}$/.test(hex)) {
    return [
      parseInt(hex.slice(0, 2), 16),
      parseInt(hex.slice(2, 4), 16),
      parseInt(hex.slice(4, 6), 16),
    ];
  }
  const m = s.match(/^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i);
  if (m) {
    return [Number(m[1]), Number(m[2]), Number(m[3])];
  }
  return null;
}

/**
 * Choose white or a near-black that maximizes WCAG contrast against a solid
 * background color. Returns `undefined` for colors we cannot parse (e.g. a
 * gradient) so the caller leaves the theme default in place.
 */
export function contrastingForeground(color: string): string | undefined {
  const rgb = _parseRgb(color);
  if (!rgb) {
    return undefined;
  }
  const lum =
    0.2126 * _toLinear(rgb[0]) +
    0.7152 * _toLinear(rgb[1]) +
    0.0722 * _toLinear(rgb[2]);
  const contrastWhite = 1.05 / (lum + 0.05);
  const contrastBlack = (lum + 0.05) / 0.05;
  return contrastWhite >= contrastBlack ? "#ffffff" : "rgba(0, 0, 0, 0.87)";
}
